import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlsNgModule } from '@seech/controls-ng';
import { Icon, IconsNgModule } from '@seech/icons-ng';
import { ModalService, PlaceholderComponent } from '@seech/ux-ng';
import { ModalShellComponent } from '@seech-sub/ux-ng';
import { Observable } from 'rxjs';
import {
  SubsiteHubItem,
  SubsiteHubResponse,
} from '../../models/subsite-hub.model';

const icons: Readonly<Record<string, Icon>> = {
  'account-subsite': 'account-circle',
  'payment-subsite': 'wallet',
  'merchant-subsite': 'briefcase',
  'affiliate-subsite': 'handshake',
  'operations-subsite': 'secured',
};

/** What each application is for, in the words a person picking one would use.
 *  Keyed like `icons` and read the same way: an application the hub adds later
 *  still lists, it just lists without a line under its name. */
const blurbs: Readonly<Record<string, string>> = {
  'account-subsite': 'Profile, security, and sign-in settings',
  'payment-subsite': 'Wallet, transactions, and subscriptions',
  'merchant-subsite': 'Offerings, partnerships, and earnings',
  'affiliate-subsite': 'Deals, links, earnings, and payouts',
  'operations-subsite': 'Internal administration and oversight',
};

@Component({
  standalone: true,
  selector: 'sub-subsite-hub-modal',
  imports: [
    ControlsNgModule,
    IconsNgModule,
    ModalShellComponent,
    PlaceholderComponent,
  ],
  templateUrl: './subsite-hub-modal.component.html',
  styleUrl: './subsite-hub-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubsiteHubModalComponent implements OnInit {
  private readonly document = inject(DOCUMENT);
  private readonly modal = inject(ModalService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  source!: Observable<SubsiteHubResponse>;
  items: SubsiteHubItem[] = [];
  loading = true;
  failed = false;

  /** Placeholder tiles for the loading state. Four is the common case; the
      grid reflows if the account turns out to have more or fewer. */
  readonly skeletons = [1, 2, 3, 4];

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.failed = false;
    this.source.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.items = response.items;
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.loading = false;
        this.failed = true;
        this.cdr.markForCheck();
      },
    });
  }

  icon(item: SubsiteHubItem): Icon {
    return icons[item.key] ?? 'dashboard-circle';
  }

  blurb(item: SubsiteHubItem): string | null {
    return blurbs[item.key] ?? null;
  }

  isCurrent(item: SubsiteHubItem): boolean {
    try {
      return (
        new URL(item.url).origin === this.document.defaultView?.location.origin
      );
    } catch {
      return false;
    }
  }

  close(): void {
    this.modal.close();
  }
}
